import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import type { DiagnosticsRecordingSession } from "@/gen/omnios/v1/diagnostics_pb";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import {
	diagnosticsClient,
	formatBytes,
	formatTimestamp,
	shortId,
} from "@/lib/diagnostics";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";

import type { ColDef } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGridSelection } from "@/hooks/use-grid-selection";
import { useServerGrid } from "@/lib/use-server-grid";

export const Route = createFileRoute("/diagnostics/sessions")({
	component: DiagnosticsSessionsPage,
});

function DiagnosticsSessionsPage() {
	const { t } = useTranslation("diagnostics");
	const navigate = useNavigate();
	useBreadcrumbs([
		{ label: t("title"), href: "/diagnostics" },
		{ label: t("sidebar_sessions") },
	]);
	const { onGridReady, onStateChanged } = useGridLayout("diagnostics-sessions");
	const { selectedRows, onSelectionChanged, ActionBar } =
		useGridSelection<DiagnosticsRecordingSession>();

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<DiagnosticsRecordingSession>({
		fetchRows: async ({ pageSize, pageToken }) => {
			const resp = await diagnosticsClient.listRecordingSessions({
				page: { pageSize, pageToken },
			});
			return {
				rows: resp.sessions ?? [],
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		colToField: {
			deviceId: "device_id",
			employeeId: "employee_id",
			captureProfile: "capture_profile",
			segmentCount: "segment_count",
			timelineBatchCount: "timeline_batch_count",
			bytesUploaded: "bytes_uploaded",
			heartbeatAt: "heartbeat_at",
			startedAt: "started_at",
		},
	});

	// -- Column definitions --
	const columnDefs = useMemo<ColDef<DiagnosticsRecordingSession>[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "id",
				headerName: t("sessions_col_session"),
				width: 120,
				pinned: "left",
				cellClass: "font-mono",
				filter: false,
				valueFormatter: (p) => shortId(p.value ?? ""),
			},
			{
				field: "deviceId",
				headerName: t("sessions_col_device"),
				width: 120,
				cellClass: "font-mono",
				valueFormatter: (p) => shortId(p.value ?? ""),
			},
			{
				field: "employeeId",
				headerName: t("sessions_col_employee"),
				width: 120,
				cellClass: "font-mono",
				valueFormatter: (p) => shortId(p.value ?? ""),
			},
			{
				field: "captureProfile",
				headerName: t("sessions_col_profile"),
				width: 130,
				cellRenderer: (p: { value: string }) => {
					if (!p.value) return "\u2014";
					return (
						<span style={{ display: "inline-block", padding: "1px 6px", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 11, lineHeight: "18px" }}>
							{t(`enum_profile_${p.value}`, { defaultValue: p.value })}
						</span>
					);
				},
			},
			{
				field: "status",
				headerName: t("sessions_col_status"),
				width: 110,
				filter: false,
				cellRenderer: (p: { value: string }) => {
					const label = t(`enum_status_${p.value ?? ""}`, {
						defaultValue: p.value ?? "",
					});
					const color =
						p.value === "error"
							? "hsl(var(--destructive))"
							: p.value === "active"
								? "hsl(var(--chart-2))"
								: "hsl(var(--muted-foreground))";
					return (
						<span style={{ display: "inline-block", padding: "1px 6px", borderRadius: 4, fontSize: 11, lineHeight: "18px", background: `color-mix(in srgb, ${color} 10%, transparent)`, color }}>
							{label}
						</span>
					);
				},
			},
			{
				field: "segmentCount",
				headerName: t("sessions_col_segments"),
				width: 100,
				type: "rightAligned",
				filter: "agNumberColumnFilter",
				cellClass: "font-tabular",
				valueFormatter: (p) => (p.value != null ? p.value.toString() : "0"),
			},
			{
				field: "timelineBatchCount",
				headerName: t("sessions_col_timeline"),
				width: 110,
				type: "rightAligned",
				filter: "agNumberColumnFilter",
				cellClass: "font-tabular",
				valueFormatter: (p) => (p.value != null ? p.value.toString() : "0"),
			},
			{
				field: "bytesUploaded",
				headerName: t("sessions_col_bytes"),
				width: 110,
				type: "rightAligned",
				filter: "agNumberColumnFilter",
				cellClass: "font-tabular",
				valueFormatter: (p) => formatBytes(p.value ?? 0),
			},
			{
				field: "heartbeatAt",
				headerName: t("sessions_col_heartbeat"),
				width: 160,
				filter: "agDateColumnFilter",
				valueFormatter: (p) => formatTimestamp(p.value),
			},
			{
				field: "startedAt",
				headerName: t("sessions_col_started"),
				width: 160,
				filter: "agDateColumnFilter",
				valueFormatter: (p) => formatTimestamp(p.value),
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	// -- Status bar --
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content:
					selectedRows.length > 0
						? `${selectedRows.length} ${t("sessions_selected", { defaultValue: "выбрано" })}`
						: `${totalRows} ${t("sessions_count_label", { defaultValue: "сессий" })}`,
				align: "left" as const,
			},
		],
		[totalRows, selectedRows.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<>
		<div className="flex items-center gap-2 px-4 py-2 shrink-0">
			<div className="relative flex-1">
				<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
				<Input
					placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
					onChange={(e) =>
						gridRef.current?.api?.setGridOption("quickFilterText", e.target.value)
					}
					className="pl-9 h-9"
				/>
			</div>
			<Button
				variant="outline"
				size="sm"
				className="h-9"
				onClick={() =>
					gridRef.current?.api?.exportDataAsCsv({
						fileName: "sessions.csv",
					})
				}
			>
				{t("toolbar_export", { defaultValue: "Экспорт" })}
			</Button>
		</div>
		<div className="flex-1 ag-theme-quartz h-full" style={{ width: "100%" }}>
			<AgGridReact<DiagnosticsRecordingSession>
				ref={gridRef}
				{...gridProps}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				localeText={AG_GRID_LOCALE_RU}
				rowSelection="multiple"
				onSelectionChanged={onSelectionChanged}
				onGridReady={onGridReady}
				onSortChanged={() => { onStateChanged(); refreshGrid(); }}
				onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
				onColumnMoved={onStateChanged}
				onColumnResized={onStateChanged}
				onColumnVisible={onStateChanged}
				onRowClicked={(e) => {
					if (e.data?.id) {
						navigate({
							to: "/diagnostics/session/$id",
							params: { id: e.data.id },
						});
					}
				}}
				suppressRowClickSelection
				animateRows={false}
				rowHeight={28}
				headerHeight={32}
				getRowId={(p) => p.data.id}
				overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("sessions_empty", { defaultValue: "Нет активных сессий" })}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("sessions_empty_desc", { defaultValue: "Сессии записи появятся здесь" })}</p></div>`}
				overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("loading", { defaultValue: "Загрузка" })}...</span>`}
			/>
		</div>
		<ActionBar />
		</>
	);
}
